<template>
  <div class="providers-modern">
    <!-- Modals -->
    <NewProvider @created="getProviders" />
    <b-modal
      size="lg"
      no-close-on-backdrop
      hide-footer
      id="import"
      modal-class="modern-modal"
    >
      <template v-slot:modal-title>
        <div class="modal-title-modern">
          <i class="fas fa-file-upload mr-2"></i>
          Importar Fornecedores
        </div>
      </template>
      <Import @success="getProviders" />
    </b-modal>

    <div class="providers-container">
      <!-- Header Section -->
      <div class="providers-header">
        <div class="header-content">
          <div class="back-button-section">
            <v-btn class="btn-back-modern" @click="$router.go(-1)">
              <i class="fas fa-arrow-left mr-2"></i>
              Voltar
            </v-btn>
          </div>

          <div class="title-section">
            <h1 class="page-title">
              <i class="fas fa-truck title-icon"></i>
              Fornecedores
            </h1>
            <p class="page-subtitle">Gerencie os fornecedores da instituição</p>
          </div>

          <div class="actions-section">
            <v-btn
              v-if="
                permission('write', 'financial_provider') ||
                permission('write', 'provider')
              "
              @click="$bvModal.show('import')"
              class="btn-import-modern"
            >
              <i class="fas fa-file-upload mr-2"></i>
              Importar
            </v-btn>
            <v-btn
              v-if="
                permission('write', 'financial_provider') ||
                permission('write', 'provider')
              "
              @click="$bvModal.show('new_provider')"
              class="btn-add-modern"
            >
              <i class="fas fa-plus mr-2"></i>
              Adicionar
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Content Section -->
      <div class="providers-content">
        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-card">
            <div class="filters-grid">
              <!-- Status Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-toggle-on filter-icon"></i>
                  Status
                </label>
                <select
                  v-model="active"
                  class="modern-form-select"
                  @change="handleChangeFilter"
                >
                  <option
                    v-for="(item, key) in active_filters"
                    :key="key"
                    :value="item.value"
                  >
                    {{ item.text }}
                  </option>
                </select>
              </div>

              <!-- Classification Filter -->
              <div class="filter-group">
                <label class="filter-label">
                  <i class="fas fa-tag filter-icon"></i>
                  Classificação
                </label>
                <select
                  v-model="classification_id"
                  class="modern-form-select"
                  @change="handleChangeFilter"
                >
                  <option value="">TODAS</option>
                  <option
                    v-for="(item, key) in classifications"
                    :key="key"
                    :value="item.id"
                  >
                    {{ item.name }}
                  </option>
                </select>
              </div>

              <!-- Search -->
              <div class="filter-group filter-search">
                <label class="filter-label">
                  <i class="fas fa-search filter-icon"></i>
                  Pesquisar
                </label>
                <input
                  type="text"
                  class="modern-form-input"
                  v-model="search"
                  @input="handleInputSearch"
                  placeholder="Digite o nome ou CPF/CNPJ do fornecedor"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Table Section -->
        <div class="table-section">
          <div class="table-card">
            <b-overlay :show="loading" opacity="0.99" class="loading-overlay">
              <!-- No Results -->
              <div
                v-show="providers.length == 0 && !loading"
                class="no-results"
              >
                <i class="fas fa-inbox no-results-icon"></i>
                <h4 class="no-results-title">Nenhum fornecedor encontrado</h4>
                <p class="no-results-text">
                  Tente ajustar os filtros ou adicione um novo fornecedor
                </p>
              </div>

              <!-- Data Table -->
              <v-data-table
                v-show="providers.length > 0"
                hide-default-footer
                disable-pagination
                :headers="headers"
                :items="providers"
                :footer-props="{
                  'items-per-page-options': [50, 75, 100],
                  'items-per-page-text': 'Mostrar',
                }"
                class="modern-table"
                :no-data-text="'Nenhum fornecedor encontrado'"
              >
                <template #[`item.company_name`]="{ item }">
                  <div class="provider-name-cell">
                    <v-tooltip bottom max-width="400">
                      <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" class="provider-name">
                          {{ item.company_name }}
                        </div>
                      </template>
                      <span>{{ item.company_name }}</span>
                    </v-tooltip>
                  </div>
                </template>

                <template #[`item.classification_name`]="{ item }">
                  <span
                    class="classification-badge"
                    v-if="item.classification_name"
                  >
                    {{ item.classification_name }}
                  </span>
                </template>

                <template #[`item.active`]="{ item }">
                  <span
                    class="status-badge"
                    :class="
                      item.active == 1 ? 'status-active' : 'status-inactive'
                    "
                  >
                    <i
                      :class="[
                        'fas',
                        item.active == 1
                          ? 'fa-check-circle'
                          : 'fa-times-circle',
                      ]"
                      class="mr-1"
                    ></i>
                    {{ item.active == 1 ? "ATIVO" : "INATIVO" }}
                  </span>
                </template>

                <template #[`item.actions`]="{ item }">
                  <v-btn
                    v-if="
                      permission('edit', 'financial_provider') ||
                      permission('edit', 'provider')
                    "
                    small
                    @click="
                      $router.push({
                        name: 'provider',
                        params: { id: item.id },
                      })
                    "
                    class="btn-action-edit"
                  >
                    <i class="fas fa-edit mr-1"></i>
                    Editar
                  </v-btn>
                </template>
              </v-data-table>
            </b-overlay>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination-section" v-if="providers.length > 0">
          <b-pagination
            v-model="page"
            :total-rows="total"
            :per-page="25"
            @input="getProviders()"
            class="modern-pagination"
            align="center"
          />
        </div>

        <!-- Errors -->
        <ErrorsMessages :errors="errors" />
      </div>
    </div>
  </div>
</template>

<script>
import NewProvider from "./NewProvider.vue";
import Import from "./Import";
import ErrorsMessages from "../../Components/ErrorsMessages.vue";
import permission from "../../../lib/permission";
import api from "../../../services/api";
import { getRestHomeSelectedFinancialAccount } from "../../../lib/functions";

export default {
  name: "Providers",

  components: {
    NewProvider,
    Import,
    ErrorsMessages,
  },

  data() {
    return {
      errors: [],
      loading: false,

      active_filters: [
        { text: "ATIVO", value: "1" },
        { text: "INATIVO", value: "0" },
      ],
      active: "1",
      page: 1,
      total: 0,
      classifications: [],
      classification_id: "",

      search: "",
      headers: [
        { text: "NOME", value: "company_name" },
        { text: "CLASSIFICAÇÃO", value: "classification_name" },
        { text: "CPF/CNPJ", value: "cpf_cnpj" },
        { text: "STATUS", value: "active" },
        { text: "AÇÕES", value: "actions", sortable: false },
      ],
      providers: [],
    };
  },

  created() {
    this.getProviders();
    this.getClassifications();
  },

  methods: {
    getRestHomeSelectedFinancialAccount,
    permission,
    handleInputSearch() {
      this.page = 1;
      this.getProviders();
    },
    handleChangeFilter() {
      this.page = 1;
      this.getProviders();
    },
    async retrieve() {
      if (this.errors.length === 0) this.getProviders();
      if (this.errors.length === 0) this.getClassifications();
    },

    async getProviders() {
      try {
        this.loading = true;

        let rest_home_id_selected_financial_account =
          this.getRestHomeSelectedFinancialAccount();

        const params = {
          rest_home_id_selected_financial_account,
          active: this.active,
          classification_id: this.classification_id,
          page: this.page,
          search: this.search,
        };

        const response = await api.get("/rest-home/providers/paginate", {
          params,
        });

        const { error, errors, providers, total } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.providers = providers;
          this.total = total;
        }
        this.loading = false;
        this.$bvModal.hide("import");
      } catch (error) {
        console.log(error);
      }
    },

    async getClassifications() {
      try {
        this.loading = true;

        let rest_home_id_selected_financial_account =
          this.getRestHomeSelectedFinancialAccount();

        const params = { rest_home_id_selected_financial_account, active: "1" };

        const response = await api.get("/rest-home/providers/classifications", {
          params,
        });
        const { error, errors, classifications } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          this.classifications = classifications;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.providers-modern {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.providers-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Header Section */
.providers-header {
  padding: 24px 0;
  background: white;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
}

.header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.title-icon {
  color: #0f65b0;
  margin-right: 12px;
}

.page-subtitle {
  color: #64748b;
  margin: 4px 0 0 0;
  font-size: 16px;
}

.actions-section {
  display: flex;
  gap: 12px;
}

/* Buttons */
.btn-back-modern {
  background: transparent !important;
  color: #64748b !important;
  border: 2px solid #e5e7eb !important;
  border-radius: 12px !important;
  padding: 12px 20px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.btn-back-modern:hover {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.btn-import-modern {
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(15, 101, 176, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-import-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(15, 101, 176, 0.4) !important;
}

.btn-add-modern {
  background: linear-gradient(135deg, #4e9130 0%, #1e884d 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  box-shadow: 0 4px 15px rgba(78, 145, 48, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-add-modern:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(78, 145, 48, 0.4) !important;
}

/* Content Section */
.providers-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

/* Filters Section */
.filters-section {
  margin-bottom: 8px;
}

.filters-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-group.filter-search {
  grid-column: 1 / -1;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.filter-icon {
  color: #6b7280;
  font-size: 14px;
}

.modern-form-input,
.modern-form-select {
  border: 2px solid #e5e7eb !important;
  border-radius: 8px !important;
  padding: 12px 16px !important;
  font-size: 14px !important;
  transition: all 0.3s ease !important;
  background: white !important;
  font-family: "WorkSans-Regular", sans-serif !important;
}

.modern-form-input:focus,
.modern-form-select:focus {
  border-color: #0f65b0 !important;
  box-shadow: 0 0 0 3px rgba(15, 101, 176, 0.1) !important;
  outline: none !important;
}

/* Table Section */
.table-section {
  margin-bottom: 8px;
}

.table-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.loading-overlay {
  border-radius: 16px;
  min-height: 300px;
}

/* No Results */
.no-results {
  text-align: center;
  padding: 60px 24px;
}

.no-results-icon {
  font-size: 64px;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.no-results-title {
  color: #1e293b;
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.no-results-text {
  color: #64748b;
  font-size: 16px;
  margin: 0;
}

/* Table Styling */
.modern-table {
  background: transparent !important;
}

.provider-name-cell {
  max-width: 300px;
}

.provider-name {
  font-weight: 500;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: help;
}

.classification-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
  border: 1px solid #818cf8;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-active {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #059669;
  border: 1px solid #34d399;
}

.status-inactive {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  border: 1px solid #f87171;
}

.btn-action-edit {
  background: linear-gradient(135deg, #1d75be 0%, #155a8a 100%) !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  font-size: 12px !important;
  box-shadow: 0 2px 8px rgba(29, 117, 190, 0.3) !important;
  transition: all 0.3s ease !important;
}

.btn-action-edit:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(29, 117, 190, 0.4) !important;
}

/* Pagination */
.pagination-section {
  display: flex;
  justify-content: center;
  padding: 16px 0;
}

.modern-pagination >>> .page-item.active .page-link {
  background-color: #0f65b0;
  border-color: #0f65b0;
  color: #fff;
  border-radius: 8px !important;
}

.modern-pagination >>> .page-link {
  border-radius: 8px;
  margin: 0 4px;
  border: 2px solid #e5e7eb;
  color: #374151;
  font-weight: 500;
}

.modern-pagination >>> .page-link:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

/* Modal Styling */
.modal-title-modern {
  display: flex;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  font-family: "WorkSans-SemiBold", sans-serif;
}

.modern-modal >>> .modal-header {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%) !important;
  border-bottom: 1px solid #e5e7eb !important;
  border-radius: 0 !important;
}

.modern-modal >>> .modal-body {
  padding: 24px !important;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .page-title {
    justify-content: center;
  }

  .actions-section {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .providers-container {
    padding: 0 16px;
  }

  .providers-header {
    padding: 16px 0;
    margin-bottom: 24px;
  }

  .header-content {
    padding: 0 16px;
  }

  .page-title {
    font-size: 24px;
  }

  .actions-section {
    flex-direction: column;
    gap: 8px;
  }

  .btn-back-modern,
  .btn-import-modern,
  .btn-add-modern {
    width: 100%;
  }

  .filters-card {
    padding: 16px;
  }

  .filters-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .filter-group.filter-search {
    grid-column: 1;
  }

  .table-card {
    border-radius: 12px;
  }

  .no-results {
    padding: 40px 16px;
  }

  .no-results-icon {
    font-size: 48px;
  }

  .no-results-title {
    font-size: 18px;
  }

  .no-results-text {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
    flex-direction: column;
    gap: 8px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .btn-back-modern,
  .btn-import-modern,
  .btn-add-modern {
    padding: 10px 16px !important;
    font-size: 14px !important;
  }

  .modern-form-input,
  .modern-form-select {
    padding: 10px 12px !important;
    font-size: 13px !important;
  }

  .filter-label {
    font-size: 13px;
  }
}

/* Override Vuetify styles */
.modern-table >>> .v-data-table__wrapper {
  border-radius: 0 !important;
}

.modern-table >>> .v-data-table thead th {
  background-color: #f8fafc !important;
  color: #374151 !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 2px solid #e5e7eb !important;
  font-family: "WorkSans-SemiBold", sans-serif !important;
}

.modern-table >>> .v-data-table tbody tr:hover {
  background-color: #f8fafc !important;
}

.modern-table >>> .v-data-table tbody td {
  font-family: "WorkSans-Regular", sans-serif !important;
}
</style>